import { Box, Button, Text, VStack } from '@chakra-ui/react';
import {
  createDateFieldResolver,
  DatesFields,
  DropdownField,
  FloatingInputField,
  MultiDropdownField,
  MultiDropdownFieldAllSelected,
  SegmentedPicker,
  TRenderValueFunc,
} from '@payler/ui-components';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TCreateReportPayloadV2,
  TCreateReportPayloadV3,
  TReportFilterV3,
  TTransactionStatus,
} from '../../../../../api/src';
import dayjs from 'dayjs';
import createLogger from 'debug';
import { useCurrencyOptions } from '../../hooks/use-currency-options';
import { useTransactionsStatusOptions } from '../../hooks/use-transactions-status-options';
import { usePaymentIndicatorsOptions } from '../../hooks/use-payment-indicators-options';
import { intersection, isEqual } from 'lodash';
import { useGetContractsCurrencies } from '../../hooks/use-get-contracts-currencies';
import {
  APM_PAYMENTS_INDICATOR,
  APM_PAYMENTS_NEW_REPORT_STATUS_OPTIONS,
  NEW_REPORT_CREDIT_STATUSES,
  NEW_REPORT_DEBIT_STATUSES,
} from '../../const';
import { usePaymentMethodsOptions } from '../../hooks/use-payment-methods-options';
import {
  useCreateReportForRuSegmentMutation,
  useCreateReportForGlobalSegmentMutation,
} from '../../hooks/reports/misc';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { useUIDateFormat } from '../../hooks/use-ui-dateFormat';
import {
  useMerchantContractsIds,
  useMerchantContractsOptions,
  useOnlyGlobalSegmentFlag,
} from '../../hooks/contracts';
import { useCheckMerchantCabinetFeatureFlag, useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { createPeriodRangeValidator } from '../../helpers/period-validation';
import { useApmPaymentsMethods } from '../../hooks/apm-payments/payment-methods';
import { useApmPaymentMethodsOptions } from '../../hooks/use-apm-payment-methods-options';
import { useDateRangeLogic } from '../../hooks/use-date-range-logic';
import { useContracts } from '../../hooks/contracts';
import { TextStyles } from '@payler/ui-theme';
import { useUserPermissions } from '../../hooks/user/users';

type PayoutSourceOption = 'api' | 'mc' | 'any';

const log = createLogger('NewReportForm');

type Props = {
  onSubmit?: VoidFunction;
};

type FormDataGlobalSegment = Omit<
  TCreateReportPayloadV3 & TReportFilterV3,
  'filter'
>;

type FormDataRuSegment = Omit<TCreateReportPayloadV2, 'transactionStatuses'> & {
  transactionStatusesDebit: TTransactionStatus[];
  transactionStatusesCredit: TTransactionStatus[];
  PayoutSources?: Array<'api' | 'mc'>;
};

type FormData = FormDataRuSegment | FormDataGlobalSegment;

type MultiSelectValue = Array<string | number> | undefined;

type SelectOption<T = string | number> = {
  label: string;
  value: T;
  disabled?: boolean;
};

const getEnabledValues = <T extends string | number>(
  options: SelectOption<T>[]
): T[] => {
  return options.filter((o) => !o.disabled).map((o) => o.value);
};

const PAYOUT_SOURCE_FORM_OPTIONS = ['api', 'mc'] as const;

const REPORT_PAYMENT_TYPES_OPTIONS = APM_PAYMENTS_INDICATOR.map((o) => ({
  label: o,
  value: o.toLowerCase(),
}));

const APM_PAYMENT_STATUS_OPTIONS = APM_PAYMENTS_NEW_REPORT_STATUS_OPTIONS.map(
  (o) => ({
    label: o,
    value: o,
  }),
);

const useDefaultsForRuSegment = () => {
  const contractIds = useMerchantContractsIds();
  const dateFormat = useUIDateFormat();
  return useMemo(
    () => ({
      contractIds,
      name: '',
      startDate: dayjs().add(-1, 'month').format(dateFormat),
      endDate: dayjs().format(dateFormat),
      transactionStatusesDebit: NEW_REPORT_DEBIT_STATUSES,
      transactionStatusesCredit: NEW_REPORT_CREDIT_STATUSES,
      paymentMethods: [],
      paymentIndicators: [],
      currencyCodes: [],
      PayoutsType: 'All' as const,
      CardLast4: '',
      Userdata: '',
      RRN: '',
      TransactionsType: 'Sbp' as const,
      PayoutSources: ['api', 'mc'] as Array<'api' | 'mc'>,
    }),
    [contractIds, dateFormat],
  );
};

const useDefaultsForGlobalSegment = () => {
  const contractIds = useMerchantContractsIds();
  const dateFormat = useUIDateFormat();
  return useMemo<FormDataGlobalSegment>(
    () => ({
      contractIds,
      name: '',
      startDate: dayjs().add(-1, 'month').format(dateFormat),
      endDate: dayjs().format(dateFormat),
      paymentMethods: [],
      currencyCodes: [],
      paymentTypes: [],
      sessionStatuses: [],
      CardLast4: '',
      Userdata: '',
      RRN: '',
    }),
    [contractIds, dateFormat],
  );
};

const useCreateResolver = () => {
  const { t } = useTranslation(['common']);
  const dateFormat = useUIDateFormat();
  const { environment } = useMerchantCabinetConfig();
  return useCallback(() => {
    const createDateFieldWithPeriodValidation = (
      otherFieldName: 'startDate' | 'endDate',
      fieldConfig: { toFieldName?: string; fromFieldName?: string },
    ) => {
      return createDateFieldResolver(t, {
        ...fieldConfig,
        maxDate: dayjs(),
        dateFormat,
      }).test(
        'periodRange',
        t('common:datePicker.rangeError', 'The period cannot exceed 1 month'),
        createPeriodRangeValidator(t, dateFormat, otherFieldName, environment, true),
      );
    };

    return yupResolver(
      yup.object({
        startDate: yup
          .string()
          .nullable()
          .test(
            'required',
            t('common:datePicker.required', 'Field cannot be empty'),
            (value) => {
              const isEmpty = !value || value === '' || String(value).trim() === '';
              return !isEmpty;
            },
          )
          .concat(
            createDateFieldWithPeriodValidation('endDate', {
          toFieldName: 'endDate',
        }),
          ),
        endDate: yup
          .string()
          .nullable()
          .test(
            'required',
            t('common:datePicker.required', 'Field cannot be empty'),
            (value) => {
              const isEmpty = !value || value === '' || String(value).trim() === '';
              return !isEmpty;
            },
          )
          .concat(
            createDateFieldWithPeriodValidation('startDate', {
              fromFieldName: 'startDate',
            }),
          ),
      }),
    );
  }, [dateFormat, t, environment]);
};

export const NewReportForm = ({ onSubmit }: Props) => {
  return (
    <Suspense fallback={null}>
      <NewReportFormComp onSubmit={onSubmit} />
    </Suspense>
  );
};

const NewReportFormComp = ({ onSubmit: outerOnSubmit }: Props) => {
  const isEnabled = useCheckMerchantCabinetFeatureFlag(
    'reports.ru_global_splitting',
  );
  const isGlobal = useOnlyGlobalSegmentFlag();
  const { environment } = useMerchantCabinetConfig();
  const isProd = environment === 'prod';

  if (!isEnabled) {
    return <RuSegmentForm onSubmit={outerOnSubmit} isProd={isProd} />;
  }

  return isGlobal ? (
    <GlobalSegmentForm onSubmit={outerOnSubmit} isProd={isProd} />
  ) : (
    <RuSegmentForm onSubmit={outerOnSubmit} isProd={isProd} />
  );
};

type FormProps = Props & { isProd: boolean };

const RuSegmentForm: FC<FormProps> = ({ onSubmit, isProd }) => {
  const { t } = useTranslation(['reports', 'fields', 'common']);
  const createReportForRuSegmentMutation =
    useCreateReportForRuSegmentMutation();
  const dateFormat = useUIDateFormat();
  const [type, setType] = useState(0);
  const defaultValues = useDefaultsForRuSegment();
  const createResolver = useCreateResolver();
  const contracts = useContracts();
  const methods = useForm<FormDataRuSegment>({
    defaultValues,
    resolver: createResolver(),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { watch, setValue } = methods;
  const payoutType = watch('PayoutsType');
  const transactionsType = watch('TransactionsType');
  const { canMakeSbpPayout } = useUserPermissions();
  const showPayoutSourceFilter =
    type === 1 && transactionsType === 'Sbp' && canMakeSbpPayout;
  
  useDateRangeLogic('startDate', 'endDate', methods);

  useEffect(() => {
    if (type !== 1 || payoutType !== 'Bulk') {
      setValue('Userdata', '', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [type, payoutType, setValue]);
  
  const handleSubmit = methods.handleSubmit(async (data: FormDataRuSegment) => {
    const isDebit = type === 0;
    const contractIds = data.contractIds;
    const isBulkPayoutAllowed = contracts?.some((c) => 
      contractIds?.includes(c.id) && c.isBulkPayoutAllowed === true
    );
    
    const { transactionStatusesCredit, transactionStatusesDebit, PayoutsType, TransactionsType, PayoutSources, ...rest } =
      data;
    const normalizedRest = {
      ...rest,
      Userdata: !isDebit && PayoutsType === 'Bulk' ? rest.Userdata : '',
    };
    const payload: TCreateReportPayloadV2 = {
      ...normalizedRest,
      transactionStatuses: isDebit
        ? transactionStatusesDebit
        : transactionStatusesCredit,
      direction: isDebit ? 'payments' : 'payouts',
    };
    if (!isDebit && isBulkPayoutAllowed && TransactionsType !== 'Sbp') {
      payload.PayoutsType = (PayoutsType || 'All') as 'Single' | 'All' | 'Bulk';
    }
    if (TransactionsType) {
      payload.TransactionsType = TransactionsType;
    }
    if (!isDebit && TransactionsType === 'Sbp') {
      const selected = (PayoutSources ?? []).filter(
        (source): source is 'api' | 'mc' =>
          (PAYOUT_SOURCE_FORM_OPTIONS as readonly string[]).includes(source),
      );
      payload.fasterPayoutSource =
        selected.length === 0 ||
        selected.length === PAYOUT_SOURCE_FORM_OPTIONS.length
          ? 'any'
          : selected[0];
    }
    log(createReportForRuSegmentMutation, payload);
    await createReportForRuSegmentMutation.mutateAsync(payload);
    onSubmit?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <VStack alignItems="stretch" spacing={3}>
          <WatchContracts />
          <SegmentedPicker
            selection={type}
            options={[t('common:debit'), t('common:credit')]}
            onSelectionChange={setType}
            isFullWidth
          />
          <FloatingInputField
            name="name"
            label={t('fields:reportName')}
            isRequired
            autoFocus
            autoComplete="off"
          />
          <Contracts />
          <SBPFilter isPayout={type === 1} />
          <VStack alignItems="stretch" spacing={1}>
            <Text textStyle={TextStyles.Caption12Regular} color="primary.300">
              {t('common:datePicker.rangeError', 'Период не может превышать 1 месяц')}
            </Text>
            <DatesFields
              labelsKeys={['fields:dateFrom', 'fields:dateTo']}
              fieldNames={['startDate', 'endDate']}
              floating
              dateFormat={dateFormat}
              maxDate={dayjs()}
              showClearButton={!isProd}
            />
          </VStack>
          {type === 1 && transactionsType !== 'Sbp' && <PayoutType />}
          {type === 0 && <StatusDebit />}
          {type === 1 && <StatusCredit />}
          {showPayoutSourceFilter && <PayoutSourceFilter />}
          {type === 0 && <PaymentMethods />}
          {type === 0 && <PaymentIndicators />}
          <Currencies />
          {type === 1 && (
            <Box
              opacity={payoutType !== 'Bulk' ? 0.5 : 1}
              pointerEvents={payoutType !== 'Bulk' ? 'none' : 'auto'}
            >
              <Userdata />
            </Box>
          )}
          <RRN />
          <Button
            type="submit"
            isLoading={createReportForRuSegmentMutation.isPending}
          >
            {t('reports:create')}
          </Button>
        </VStack>
      </form>
    </FormProvider>
  );
};

const GlobalSegmentForm: FC<FormProps> = ({ onSubmit, isProd }) => {
  const { t } = useTranslation(['fields', 'reports']);
  const createReportForGlobalSegmentMutation =
    useCreateReportForGlobalSegmentMutation();
  const dateFormat = useUIDateFormat();
  const defaultValues = useDefaultsForGlobalSegment();
  const createResolver = useCreateResolver();
  const methods = useForm<FormDataGlobalSegment>({
    defaultValues,
    resolver: createResolver(),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  
  useDateRangeLogic('startDate', 'endDate', methods);

  const handleSubmit = methods.handleSubmit(
    async (data: FormDataGlobalSegment) => {
      const { name, ...rest } = data;
      log(createReportForGlobalSegmentMutation, data);
      await createReportForGlobalSegmentMutation.mutateAsync({
        name,
        filter: rest,
      });
      onSubmit?.();
    },
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <VStack alignItems="stretch" spacing={3}>
          <WatchContracts isApm />
          <FloatingInputField
            name="name"
            label={t('fields:reportName')}
            isRequired
            autoFocus
            autoComplete="off"
          />
          <Contracts />
          <VStack alignItems="stretch" spacing={1}>
            <Text textStyle={TextStyles.Caption12Regular} color="primary.300">
              {t('common:datePicker.rangeError', 'Период не может превышать 1 месяц')}
            </Text>
            <DatesFields
              labelsKeys={['fields:dateFrom', 'fields:dateTo']}
              fieldNames={['startDate', 'endDate']}
              floating
              dateFormat={dateFormat}
              maxDate={dayjs()}
              showClearButton={!isProd}
            />
          </VStack>
          <ApmPaymentStatuses />
          <ApmPaymentMethods />
          <PaymentTypes />
          <Currencies isApm />
          <Userdata />
          <RRN />
          <Button
            type="submit"
            isLoading={createReportForGlobalSegmentMutation.isPending}
          >
            {t('reports:create')}
          </Button>
        </VStack>
      </form>
    </FormProvider>
  );
};

const PaymentTypes = () => {
  const { t } = useTranslation(['fields']);
  return (
    <MultiDropdownField
      options={REPORT_PAYMENT_TYPES_OPTIONS}
      fieldName="paymentTypes"
      floating
      label={t('fields:paymentIndicator')}
    />
  );
};

const ApmPaymentStatuses = () => {
  const { t } = useTranslation(['fields']);
  return (
    <MultiDropdownField
      options={APM_PAYMENT_STATUS_OPTIONS}
      fieldName="sessionStatuses"
      floating
      label={t('fields:transactionStatus')}
    />
  );
};

const PaymentMethods = () => {
  const { options, fieldName } = usePaymentMethodsOptions();
  return (
    <MultiDropdownField
      options={options}
      fieldName="paymentMethods"
      floating
      label={fieldName}
    />
  );
};

const ApmPaymentMethods = () => {
  const { watch } = useFormContext();
  const contractIds = watch('contractIds');
  const { options, fieldName } = useApmPaymentMethodsOptions(contractIds);

  return (
    <MultiDropdownField
      options={options}
      fieldName="paymentMethods"
      floating
      label={fieldName}
    />
  );
};

const PaymentIndicators = () => {
  const { options, fieldName } = usePaymentIndicatorsOptions();
  return (
    <MultiDropdownField
      options={options}
      fieldName="paymentIndicators"
      floating
      label={fieldName}
      noEllipsis
    />
  );
};

const StatusDebit = () => {
  const { fieldName, options, selectAllText } = useTransactionsStatusOptions(
    NEW_REPORT_DEBIT_STATUSES,
  );
  const { getValues, setValue } = useFormContext<FormData>();

  useEffect(() => {
    if (!options?.length) return;

    const enabledValues = getEnabledValues(options);

    const current =
      (getValues('transactionStatusesDebit') as MultiSelectValue) ?? [];

    const isAll =
      current.length === enabledValues.length &&
      enabledValues.every((v) => current.includes(v));

    if (!isAll) {
      setValue('transactionStatusesDebit', enabledValues, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [options, getValues, setValue]);

  return (
    <MultiDropdownFieldAllSelected
      fieldName="transactionStatusesDebit"
      options={options}
      preset="mc2"
      label={fieldName}
      selectAllLabel={selectAllText}
      nullValueAsAllSelected
      selectAllOptionsBehaviour="selected"
      showSelectAllLabelAsValue
    />
  );
};

const StatusCredit = () => {
  const { options, fieldName, selectAllText } = useTransactionsStatusOptions(
    NEW_REPORT_CREDIT_STATUSES,
  );
  const { getValues, setValue } = useFormContext<FormData>();

  useEffect(() => {
    if (!options?.length) return;

    const enabledValues = getEnabledValues(options);

    const current =
      (getValues('transactionStatusesCredit') as MultiSelectValue) ?? [];

    const isAll =
      current.length === enabledValues.length &&
      enabledValues.every((v) => current.includes(v));

    if (!isAll) {
      setValue('transactionStatusesCredit', enabledValues, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [options, getValues, setValue]);

  return (
    <MultiDropdownFieldAllSelected
      fieldName="transactionStatusesCredit"
      options={options}
      preset="mc2"
      label={fieldName}
      selectAllLabel={selectAllText}
      nullValueAsAllSelected
      selectAllOptionsBehaviour="selected"
      showSelectAllLabelAsValue
    />
  );
};

const Contracts = () => {
  const { t } = useTranslation(['fields', 'common']);
  const options = useMerchantContractsOptions();
  const isSingleContract = options.length === 1;

  const renderSingleValueCallback: TRenderValueFunc = useCallback(
    ({ props }) => props.options[0]?.label || '',
    [],
  );

  const renderAllSelectedValue: TRenderValueFunc = useCallback(
    ({ props }) => {
      const selectedValues = (props.value ?? []) as Array<string | number>;
      const enabledCount = getEnabledValues(props.options as SelectOption[]).length;

      const allSelected = selectedValues.length > 0 && selectedValues.length === enabledCount;
      if (allSelected) {
        return t('common:allContracts');
      }

      if (selectedValues.length === 0) {
        return '';
      }

      return selectedValues
        .map(value => options.find(opt => opt.value === value)?.label ?? String(value))
        .join(', ');
    },
    [options, t],
  );
  
  const { getValues, setValue } = useFormContext<FormData>();

  useEffect(() => {
    if (!options?.length) return;
  
    const enabledValues = getEnabledValues(options);
  
    const current = (getValues('contractIds') as MultiSelectValue) ?? [];
  
    const isAll =
      current.length === enabledValues.length &&
      enabledValues.every(v => current.includes(v));
  
    if (!isAll) {
      setValue('contractIds', enabledValues, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [options, getValues, setValue]);
  
  return (
    <MultiDropdownFieldAllSelected
      fieldName="contractIds"
      options={options}
      renderValue={
        isSingleContract
          ? renderSingleValueCallback
          : renderAllSelectedValue
      }
      label={t('fields:contract')}
      selectAllLabel={!isSingleContract ? t('common:allContracts') : undefined}
      preset={!isSingleContract ? 'mc3' : undefined}
      hideClearButton
      nullValueAsAllSelected
      selectAllOptionsBehaviour="selected"
      showSelectAllLabelAsValue
    />
  );
};

const SBPFilter: FC<{ isPayout: boolean }> = ({ isPayout }) => {
  const { t } = useTranslation(['fields']);
  const { watch, setValue } = useFormContext<FormDataRuSegment>();
  const contractIds = watch('contractIds');
  const contracts = useContracts();
  const TransactionsType = watch('TransactionsType');
  
  const isSbpAvailable = useMemo(() => {
    if (!contracts) return false;
    // Payout report filters must not depend on the selected contracts.
    const relevantContracts = isPayout
      ? contracts
      : contracts.filter((c) => contractIds?.includes(c.id));
    return relevantContracts.some((c) => c.fasterPaymentsEnabled === true);
  }, [contracts, contractIds, isPayout]);

  const options = useMemo(
    () => [
      { value: 'NonSbp', label: t('fields:sbpFilter.NonSbp') },
      { value: 'Sbp', label: t('fields:sbpFilter.Sbp') },
    ],
    [t],
  );

  useEffect(() => {
    if (!contracts) return;

    if (isSbpAvailable && !TransactionsType) {
      setValue('TransactionsType', 'Sbp', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    } else if (!isSbpAvailable && TransactionsType !== 'NonSbp') {
      setValue('TransactionsType', 'NonSbp', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [contracts, isSbpAvailable, TransactionsType, setValue]);

  if (!isSbpAvailable) {
    return null;
  }

  return (
    <DropdownField
      options={options}
      fieldName="TransactionsType"
      floating
      label={t('fields:sbpFilter')}
      hideClearButton
    />
  );
};

const PayoutSourceFilter = () => {
  const { t } = useTranslation(['fields']);
  const { getValues, setValue } = useFormContext<FormDataRuSegment>();
  const options = useMemo(
    () => [
      { value: 'api' as const, label: t('fields:fasterPayoutSource.api') },
      { value: 'mc' as const, label: t('fields:fasterPayoutSource.mc') },
    ],
    [t],
  );

  useEffect(() => {
    const enabledValues = getEnabledValues(options);
    const current =
      (getValues('PayoutSources') as MultiSelectValue) ?? [];
    const isAll =
      current.length === enabledValues.length &&
      enabledValues.every((value) => current.includes(value));

    if (!isAll && current.length === 0) {
      setValue('PayoutSources', enabledValues, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [options, getValues, setValue]);

  return (
    <MultiDropdownFieldAllSelected
      fieldName="PayoutSources"
      options={options}
      preset="mc2"
      label={t('fields:fasterPayoutSource')}
      selectAllLabel={t('fields:fasterPayoutSource.any')}
      nullValueAsAllSelected
      selectAllOptionsBehaviour="selected"
      showSelectAllLabelAsValue
    />
  );
};

const PayoutType = () => {
  const { t } = useTranslation(['fields', 'typePayouts']);
  const { watch, setValue } = useFormContext<FormDataRuSegment>();
  const contractIds = watch('contractIds');
  const contracts = useContracts();
  const PayoutsType = watch('PayoutsType');
  
  const isBulkPayoutAllowed = useMemo(() => {
    if (!contracts || !contractIds) return false;
    const selectedContracts = contracts.filter((c) =>
      contractIds.includes(c.id),
    );
    return selectedContracts.some((c) => c.isBulkPayoutAllowed === true);
  }, [contracts, contractIds]);

  const options = useMemo(
    () => [
      { value: 'Single', label: t('typePayouts:Single') },
      { value: 'All', label: t('typePayouts:All') },
      { value: 'Bulk', label: t('typePayouts:Bulk') },
    ],
    [t],
  );

  useEffect(() => {
    if (isBulkPayoutAllowed && !PayoutsType) {
      setValue('PayoutsType', 'All', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [isBulkPayoutAllowed, PayoutsType, setValue]);

  if (!isBulkPayoutAllowed) {
    return null;
  }

  return (
    <DropdownField
      options={options}
      fieldName="PayoutsType"
      floating
      label={t('fields:payoutType')}
      hideClearButton
    />
  );
};

const Currencies: FC<{ isApm?: boolean }> = ({ isApm }) => {
  const { watch } = useFormContext<FormData>();
  const contractIds = watch('contractIds');
  const { options, fieldName } = useCurrencyOptions({
    contractIds,
    isApm,
  });

  return (
    <MultiDropdownField
      options={options}
      fieldName="currencyCodes"
      floating
      label={fieldName}
      noEllipsis
    />
  );
};

const WatchContracts: FC<{ isApm?: boolean }> = ({ isApm }) => {
  const { watch, setValue } = useFormContext<FormData>();
  const [contractIds, currencyCodes, paymentMethods] = watch([
    'contractIds',
    'currencyCodes',
    'paymentMethods',
  ]);
  const getCurrencies = useGetContractsCurrencies(isApm);
  const availablePaymentsMethods = useApmPaymentsMethods(contractIds);

  useEffect(() => {
    const currencies = getCurrencies(contractIds);
    const resCurrencies = intersection(currencyCodes, currencies);

    if (!isEqual(currencyCodes?.sort(), resCurrencies?.sort())) {
      setValue('currencyCodes', resCurrencies);
    }
  }, [contractIds, currencyCodes, getCurrencies, setValue]);

  useEffect(() => {
    if (!isApm) {
      return;
    }
    const resPaymentsMethods = intersection(
      availablePaymentsMethods,
      paymentMethods,
    );
    if (!isEqual(paymentMethods?.sort(), resPaymentsMethods?.sort())) {
      setValue('paymentMethods', resPaymentsMethods);
    }
  }, [availablePaymentsMethods, isApm, paymentMethods, setValue]);

  return null;
};

const Userdata = () => {
  const { t } = useTranslation(['fields']);
  return (
    <FloatingInputField
      name="Userdata"
      label={t('fields:description')}
      placeholder={t('fields:descriptionPlaceholder')}
    />
  );
};

const RRN = () => {
  const { t } = useTranslation(['fields']);
  return (
    <FloatingInputField
      name="RRN"
      label={t('fields:rrn')}
      placeholder={t('fields:rrnPlaceholder')}
    />
  );
};
